import { useState, useCallback } from 'react';
import { SubscriptionData, PricingPlan, PaymentDetails, PaymentRecord, Payment } from '../types/profile';
import subscriptionService from '../services/subscription'; // Pour les mocks
import subscriptionApiService from '../services/subscriptionApi'; // Pour l'API réelle
import { useToast } from '../hooks/useToast';
import { useOnceEffect } from './useOnceEffect';
import { Subscription, CustomerTokenBalance, PlanFeatures } from '../types/subscription';

// Fonction pour convertir les données API en format attendu par l'UI
const convertApiSubscriptionToUI = (
  apiSubscription: Subscription,
  tokenBalance?: CustomerTokenBalance,
  payments?: Payment[]
): SubscriptionData => {
  return {
    id: apiSubscription.id,
    userId: apiSubscription.customerId,
    currentPlan: apiSubscription.plan.name,
    tokenBalance: tokenBalance ? tokenBalance.remainingTokens : 0,
    tokenTotal: tokenBalance ? tokenBalance.totalTokens : 0,
    paymentHistory: payments ? payments.map((payment: Payment): PaymentRecord => ({
      id: payment.id,
      date: new Date(payment.date).toISOString().split('T')[0],
      amount: payment.amount,
      method: payment.method,
      plan: payment.plan,
      status: payment.status,
      receiptUrl: payment.receiptUrl || '#'
    })) : []
  };
};

// Fonction pour convertir les features de l'API en format attendu par l'UI
const convertApiFeaturesToUI = (apiFeatures: PlanFeatures): string[] => {
  const features: string[] = [];
  
  // Convertir l'objet des fonctionnalités en tableau de strings
  Object.entries(apiFeatures).forEach(([key, value]) => {
    if (value.enabled) {
      let feature = value.description || key.replace(/_/g, ' ');
      if (value.limit) {
        feature += ` (${value.limit})`;
      }
      features.push(feature);
    }
  });
  
  return features;
};

// Helper to create a consistent mock user ID, mirroring the service
const MOCK_USER_ID = 'user_auth0_mock_id';

interface UseSubscriptionOptions {
  preferMock?: boolean;
}

export const useSubscription = (options: UseSubscriptionOptions = { preferMock: false }) => {
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [usingMockData, setUsingMockData] = useState<boolean>(options.preferMock || false);
  
  const toast = useToast();

  const loadSubscriptionData = useCallback(async () => {
    try {
      setIsLoading(true);
      
      let subscriptionData: SubscriptionData | null = null;
      
      if (!options.preferMock) {
        try {
          // Essayer d'abord d'obtenir les données depuis l'API
          console.log('🌐 Tentative de récupération des données d\'abonnement depuis l\'API');
          const apiData = await subscriptionApiService.getCurrentSubscription();
          
          // Récupération des données supplémentaires nécessaires
          const tokenBalance = await subscriptionApiService.getTokenBalance();
          const payments = (await subscriptionApiService.getPaymentHistory()).payments;
          
          // Conversion du format API en format attendu par l'UI
          subscriptionData = convertApiSubscriptionToUI(apiData, tokenBalance, payments);
          
          setUsingMockData(false);
          console.log('✅ Données d\'abonnement récupérées depuis l\'API avec succès');
        } catch (apiError) {
          console.warn(`⚠️ Erreur API, utilisation des données mockées: ${apiError instanceof Error ? apiError.message : 'Erreur inconnue'}`);
          // En cas d'échec de l'API, fallback sur les données mockées
          subscriptionData = await subscriptionService.getSubscriptionData(MOCK_USER_ID);
          setUsingMockData(true);
        }
      } else {
        // Utilisation directe des données mockées si préférées
        console.log('🧪 Utilisation préférée des données mockées pour l\'abonnement');
        subscriptionData = await subscriptionService.getSubscriptionData(MOCK_USER_ID);
        setUsingMockData(true);
      }
      
      setSubscription(subscriptionData);
      setError(null);
    } catch (err) {
      const error = err as Error;
      setError(error.message);
      console.error('❌ Erreur lors de la récupération des données d\'abonnement:', error);
    } finally {
      setIsLoading(false);
    }
  }, [options.preferMock]);

  const loadPlans = useCallback(async () => {
    try {
      let availablePlans: PricingPlan[];
      
      if (!usingMockData) {
        try {
          // Essayer d'abord l'API
          console.log('🌐 Récupération des plans depuis l\'API');
          const apiPlans = await subscriptionApiService.getPlans();
          
          // Conversion du format API en format attendu par l'UI
          availablePlans = apiPlans.map(plan => ({
            name: plan.name,
            description: plan.description || '',
            price: (plan.monthlyPriceUSD || 0).toString(),
            features: convertApiFeaturesToUI(plan.features),
          }));
          
          console.log('✅ Plans récupérés depuis l\'API avec succès');
        } catch (apiError) {
          console.warn(`⚠️ Erreur API, utilisation des plans mockés: ${apiError instanceof Error ? apiError.message : 'Erreur inconnue'}`);
          // En cas d'échec, utilisation des plans mockés
          availablePlans = await subscriptionService.getAvailablePlans();
          setUsingMockData(true);
        }
      } else {
        // Utilisation directe des plans mockés si préféré
        console.log('🧪 Utilisation préférée des plans mockés');
        availablePlans = await subscriptionService.getAvailablePlans();
      }
      
      setPlans(availablePlans);
    } catch (err) {
      const error = err as Error;
      setError(error.message);
      console.error('❌ Erreur lors de la récupération des plans:', error);
    }
  }, [usingMockData]);

  // Charger les données une seule fois pour éviter les boucles
  useOnceEffect(() => {
    loadSubscriptionData();
    loadPlans();
  });

  const changePlan = async (newPlanId: string, paymentMethod: 'card' | 'mobile' | 'manual') => {
    if (!subscription) return;
    
    try {
      setIsLoading(true);
      
      let updatedSubscription: SubscriptionData;
      
      try {
        if (usingMockData) {
          throw new Error('Utilisant des données mockées, continuant avec le mock');
        }
        
        // Essayer d'abord l'API
        console.log('📤 Changement de plan via l\'API...');
        
        // Mise en forme des données pour l'API
        const apiData = await subscriptionApiService.changeSubscriptionPlan({
          planId: newPlanId
        });
        
        // Récupérer les données mises à jour
        const tokenBalance = await subscriptionApiService.getTokenBalance();
        const payments = (await subscriptionApiService.getPaymentHistory()).payments;
        
        // Conversion du format API en format attendu par l'UI
        updatedSubscription = convertApiSubscriptionToUI(apiData, tokenBalance, payments);
        
        console.log('✅ Plan changé via l\'API avec succès');
        
        // Mettre à jour également le mock pour garder la cohérence
        await subscriptionService.changePlan(subscription.userId, newPlanId, paymentMethod);
      } catch (apiError) {
        console.warn(`⚠️ Impossible de changer de plan via l'API, utilisation du mock: ${apiError instanceof Error ? apiError.message : 'Erreur inconnue'}`);
        // En cas d'échec, changement de plan via le mock
        updatedSubscription = await subscriptionService.changePlan(subscription.userId, newPlanId, paymentMethod);
        setUsingMockData(true);
      }
      
      setSubscription(updatedSubscription);
      setError(null);
      
      toast.showToast?.({
        type: 'success',
        title: 'Plan modifié',
        message: `Votre abonnement a été mis à jour vers le plan ${newPlanId}`
      });
    } catch (err) {
      const error = err as Error;
      setError(error.message);
      console.error('❌ Erreur lors du changement de plan:', error);
      
      toast.showToast?.({
        type: 'error',
        title: 'Erreur',
        message: `Échec du changement de plan: ${error.message}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  const rechargeTokens = async (paymentDetails: PaymentDetails) => {
    if (!subscription) return;
    
    try {
      setIsLoading(true);
      
      let updatedSubscription: SubscriptionData;
      
      try {
        if (usingMockData) {
          throw new Error('Utilisant des données mockées, continuant avec le mock');
        }
        
        // Essayer d'abord l'API
        console.log('📤 Achat de tokens via l\'API...');
        
        // Déterminer le montant numérique
        const amount = typeof paymentDetails.amount === 'string' 
          ? parseFloat(paymentDetails.amount) 
          : paymentDetails.amount || 0;
        
        // Extraction des informations de la carte si disponibles
        let expiryMonth: number | undefined;
        let expiryYear: number | undefined;
        if (paymentDetails.expiryDate && paymentDetails.expiryDate.includes('/')) {
          const parts = paymentDetails.expiryDate.split('/');
          expiryMonth = parseInt(parts[0], 10);
          expiryYear = parseInt(parts[1], 10);
          if (expiryYear < 100) expiryYear += 2000; // Convertir 23 en 2023
        }
        
        // Mise en forme des données pour l'API
        await subscriptionApiService.purchaseTokens({
          amount: amount,
          paymentMethod: {
            type: paymentDetails.paymentMethod as 'card' | 'mobile',
            mobileMoneyProvider: paymentDetails.mobileMoneyProvider,
            mobileMoneyNumber: paymentDetails.mobileMoneyNumber,
            cardNumber: paymentDetails.cardNumber,
            expiryMonth: expiryMonth,
            expiryYear: expiryYear,
            cvc: paymentDetails.cvv,
            name: paymentDetails.cardNumber ? 'Card Holder' : undefined
          }
        });
        
        // Récupérer les données mises à jour
        const tokenBalance = await subscriptionApiService.getTokenBalance();
        
        // Mettre à jour l'abonnement avec les nouvelles données de tokens
        updatedSubscription = {
          ...subscription,
          tokenBalance: tokenBalance.remainingTokens,
          tokenTotal: tokenBalance.totalTokens
        };
        
        console.log('✅ Tokens achetés via l\'API avec succès');
        
        // Mettre à jour également le mock pour garder la cohérence
        await subscriptionService.rechargeTokens(subscription.userId, paymentDetails);
      } catch (apiError) {
        console.warn(`⚠️ Impossible d'acheter des tokens via l'API, utilisation du mock: ${apiError instanceof Error ? apiError.message : 'Erreur inconnue'}`);
        // En cas d'échec, achat via le mock
        updatedSubscription = await subscriptionService.rechargeTokens(subscription.userId, paymentDetails);
        setUsingMockData(true);
      }
      
      setSubscription(updatedSubscription);
      setError(null);
      
      toast.showToast?.({
        type: 'success',
        title: 'Tokens achetés',
        message: `Votre compte a été rechargé avec succès`
      });
    } catch (err) {
      const error = err as Error;
      setError(error.message);
      console.error('❌ Erreur lors de l\'achat de tokens:', error);
      
      toast.showToast?.({
        type: 'error',
        title: 'Erreur',
        message: `Échec de l'achat de tokens: ${error.message}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    subscription,
    plans,
    isLoading,
    error,
    usingMockData, // Nouvel indicateur pour savoir si on utilise les données mockées
    changePlan,
    rechargeTokens,
    reload: loadSubscriptionData,
    refreshPlans: loadPlans
  };
};
